import { useEffect, useState, useCallback } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { API, logErr, inr } from "../../lib/api";

const COLORS = ["#D4AF37", "#F3E5AB", "#B8962E", "#8a7424"];

export default function AdminAnalytics() {
  const [from, setFrom] = useState(() => new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10));
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [d, setD] = useState(null);

  const load = useCallback(() => API.get("/admin/analytics", { params: { from_date: from, to_date: to } })
    .then((r) => setD(r.data)).catch((err) => logErr("Failed to load analytics:", err)), [from, to, API, logErr]);
  useEffect(() => { load(); document.title = "Analytics | Admin"; }, [load]);

  const inputCls = "bg-ink border border-[#262626] px-3 py-2.5 text-sm outline-none focus:border-gold transition-colors";

  return (
    <div data-testid="admin-analytics">
      <h1 className="font-display font-black text-3xl tracking-tighter mb-6">Analytics</h1>
      <div className="flex flex-wrap items-end gap-3 mb-8">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">From</p>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputCls} data-testid="analytics-from" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">To</p>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} data-testid="analytics-to" />
        </div>
        <button onClick={load} data-testid="analytics-apply-btn" className="btn-gold px-6 py-2.5 text-xs font-bold uppercase tracking-widest">Apply</button>
      </div>

      {!d ? <div className="text-muted-foreground">Loading...</div> : (
        <>
          <div className="grid grid-cols-3 gap-4 mb-6">
            {[["Revenue", inr(d.total_revenue)], ["Orders", d.total_orders], ["Avg order", inr(d.avg_order_value)]].map(([l, v]) => (
              <div key={l} className="border border-[#262626] bg-ink-surface p-5" data-testid={`analytics-${l.toLowerCase().replace(" ", "-")}`}>
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{l}</p>
                <p className="font-display font-black text-2xl mt-2 gold-text">{v}</p>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-4 mb-6">
            <div className="border border-[#262626] bg-ink-surface p-5" data-testid="analytics-by-day">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">Sales by day</p>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={d.sales_by_day}>
                  <XAxis dataKey="date" stroke="#52525b" fontSize={9} tickLine={false} axisLine={false} tickFormatter={(v) => v.slice(5)} />
                  <YAxis stroke="#52525b" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(v) => `₹${v >= 1000 ? `${v / 1000}k` : v}`} />
                  <Tooltip contentStyle={{ background: "#121212", border: "1px solid #262626", fontSize: 12 }} formatter={(v) => [inr(v), "Revenue"]} />
                  <Bar dataKey="revenue" fill="#D4AF37" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="border border-[#262626] bg-ink-surface p-5" data-testid="analytics-by-category">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">Revenue by category</p>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={d.sales_by_category} dataKey="revenue" nameKey="category" cx="50%" cy="50%" outerRadius={85} label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`} fontSize={10}>
                    {d.sales_by_category.map((entry, i) => <Cell key={entry.category} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#121212", border: "1px solid #262626", fontSize: 12 }} formatter={(v) => [inr(v), "Revenue"]} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="border border-[#262626] bg-ink-surface p-5" data-testid="analytics-products">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">Product performance</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[480px]">
                <thead><tr className="text-left text-[10px] uppercase tracking-[0.2em] text-muted-foreground border-b border-[#262626]">
                  <th className="pb-3">#</th><th className="pb-3">Product</th><th className="pb-3">Units</th><th className="pb-3">Revenue</th>
                </tr></thead>
                <tbody>
                  {d.product_performance.map((p, i) => (
                    <tr key={p.name} className="border-b border-[#262626]">
                      <td className="py-2.5 text-gold font-display font-black">{i + 1}</td>
                      <td className="py-2.5 text-xs font-bold">{p.name}</td>
                      <td className="py-2.5 text-xs">{p.qty}</td>
                      <td className="py-2.5 text-xs font-bold text-gold">{inr(p.revenue)}</td>
                    </tr>
                  ))}
                  {d.product_performance.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-xs text-muted-foreground">No sales in this range.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
